'use strict';

import angular from 'angular';

/**
 * @ngdoc service
 * @name observatoireApp.service:MetaService
 * @description
 * # MetaService
 * Service gérant les métadonnées de la page (titre, description, mots-clés)
 */
angular.module('observatoireApp')
  .service('MetaService', ['$rootScope', '$window', '$document', function($rootScope, $window, $document) {
    
    // Valeurs par défaut
    const defaultTitle = 'Observatoire Tchadien des Finances Publiques';
    const defaultDescription = 'Plateforme de données ouvertes pour les finances publiques du Tchad';
    const defaultKeywords = 'observatoire, tchad, finances, pétrole, transparence';
    const siteName = 'Observatoire Tchadien';
    
    // Valeurs courantes (état interne)
    const meta = {
      title: defaultTitle,
      description: defaultDescription,
      keywords: defaultKeywords,
      image: null
    };
    
    /**
     * Définit le titre de la page
     * @param {string} title Nouveau titre
     */
    this.setTitle = function(title) {
      meta.title = title || defaultTitle;
      
      // Mettre à jour le titre du document
      $document[0].title = meta.title;
      
      // Émettre un événement pour notifier les changements
      $rootScope.$emit('metaService:titleChanged', meta.title);
    };
    
    /**
     * Obtient le titre actuel
     * @returns {string} Titre actuel
     */
    this.getTitle = function() {
      return meta.title;
    };
    
    /**
     * Définit la description de la page
     * @param {string} description Nouvelle description
     */
    this.setDescription = function(description) {
      meta.description = description || defaultDescription;
      
      // Mettre à jour les balises meta
      updateMetaTag('description', meta.description);
      updateMetaTag('og:description', meta.description);
      
      $rootScope.$emit('metaService:descriptionChanged', meta.description);
    };
    
    /**
     * Obtient la description actuelle
     * @returns {string} Description actuelle
     */
    this.getDescription = function() {
      return meta.description;
    };
    
    /**
     * Définit les mots-clés de la page
     * @param {string|Array} keywords Nouveaux mots-clés (chaîne ou tableau)
     */
    this.setKeywords = function(keywords) {
      if (Array.isArray(keywords)) {
        meta.keywords = keywords.join(', ');
      } else {
        meta.keywords = keywords || defaultKeywords;
      }
      
      updateMetaTag('keywords', meta.keywords);
      $rootScope.$emit('metaService:keywordsChanged', meta.keywords);
    };
    
    /**
     * Obtient les mots-clés actuels
     * @returns {string} Mots-clés actuels
     */
    this.getKeywords = function() {
      return meta.keywords;
    };
    
    /**
     * Définit l'image à partager sur les réseaux sociaux
     * @param {string} url URL de l'image
     */
    this.setImage = function(url) {
      meta.image = url;
      
      if (url) {
        updateMetaTag('og:image', url);
        updateMetaTag('twitter:image', url);
      } else {
        removeMetaTag('og:image');
        removeMetaTag('twitter:image');
      }
      
      $rootScope.$emit('metaService:imageChanged', meta.image);
    };
    
    /**
     * Réinitialise toutes les métadonnées aux valeurs par défaut
     */
    this.reset = function() {
      this.setTitle(defaultTitle);
      this.setDescription(defaultDescription);
      this.setKeywords(defaultKeywords);
      this.setImage(null);
    };
    
    /**
     * Configure toutes les métadonnées en une seule fois
     * @param {Object} options Options contenant title, description, keywords, image
     */
    this.setAll = function(options = {}) {
      if (options.title) this.setTitle(options.title);
      if (options.description) this.setDescription(options.description);
      if (options.keywords) this.setKeywords(options.keywords);
      if (options.image) this.setImage(options.image);
    };
    
    /**
     * Met à jour ou crée une balise meta dans le document
     * @param {string} name Nom de la balise meta
     * @param {string} content Contenu de la balise
     * @private
     */
    function updateMetaTag(name, content) {
      const head = $document[0].head;
      let metaTag;
      
      // Chercher d'abord par le nom
      metaTag = head.querySelector(`meta[name="${name}"]`);
      
      // Si non trouvé, chercher par propriété og
      if (!metaTag) {
        metaTag = head.querySelector(`meta[property="${name}"]`);
      }
      
      if (metaTag) {
        // Mettre à jour la balise existante
        metaTag.setAttribute('content', content);
      } else {
        // Créer une nouvelle balise
        metaTag = $document[0].createElement('meta');
        
        // Déterminer si c'est une propriété Open Graph
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          metaTag.setAttribute('property', name);
        } else {
          metaTag.setAttribute('name', name);
        }
        
        metaTag.setAttribute('content', content);
        head.appendChild(metaTag);
      }
    }
    
    /**
     * Supprime une balise meta du document
     * @param {string} name Nom de la balise à supprimer
     * @private
     */
    function removeMetaTag(name) {
      const head = $document[0].head;
      const metaTagByName = head.querySelector(`meta[name="${name}"]`);
      
      if (metaTagByName) {
        head.removeChild(metaTagByName);
      }
      
      const metaTagByProperty = head.querySelector(`meta[property="${name}"]`);
      
      if (metaTagByProperty) {
        head.removeChild(metaTagByProperty);
      }
    }
    
    // Initialiser les balises Open Graph au démarrage
    updateMetaTag('og:site_name', siteName);
    updateMetaTag('og:type', 'website');
    updateMetaTag('twitter:card', 'summary');
    
    // Exposer le service au scope global pour débogage (optionnel)
    $window.MetaService = this;
    
    return this;
  }]);
